import {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import type {RouteLocationAsRelative} from "vue-router";
import {type CardData, EntityAction} from "@/domain/type/entity.type.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useEntitiesStore} from "@/stores/entities.store.ts";
import {RequestEntityState, RequestEntityStatus, type SourceDetails} from "@/domain/type/synchro.api.type.ts";
import type {CourseDetails} from "@/domain/type/course.type.ts";
import {Source} from "@/domain/type/source.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";
import CourseConfig from "@/application/typeConfig/CourseConfig.ts";

export class Course extends AbstractEntity {
  cardIcon = 'mdi-school'
  left: CourseDetails = {
    link: "",
    product: "",
    id: '',
    numGroup: '',
    startDate: '',
    endDate: '',
    state: '',
    site: ''
  }
  right: CourseDetails = {
    link: "",
    product: "",
    id: '',
    numGroup: '',
    startDate: '',
    endDate: '',
    state: '',
    site: ''
  }

  constructor(
    uuid: string,
    leftFromApi: SourceDetails[],
    rightFromApi: SourceDetails[][],
    state: RequestEntityState,
    status: RequestEntityStatus
  ) {
    super(uuid, leftFromApi, rightFromApi, state, status);
    this.left = this.hydrateDetails(leftFromApi);
    this.right = this.hydrateDetails(rightFromApi[0] ?? []);
    this.leftLink = this.left.link;
    if (this.right.link === '') {
      if (import.meta.env.VITE_AMMON_BASE_URL_OLD === import.meta.env.VITE_AMMON_BASE_URL) {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL_OLD + '/AMMON/GESFORM/ActionsDeFormation/pages/ActionRecherche.aspx';
      }else{
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL + '/BO/s/GESFORM/ActionsDeFormation/pages/ActionRecherche.aspx';
      }
    } else {
      this.rightLink = this.right.link;
    }
  }

  getCreateActionText(srcRight: Source): string {
    const target = srcRight.charAt(0).toUpperCase() + srcRight.slice(1).toLowerCase();
    return 'Êtes-vous certain de vouloir importer l\'action de formation dans ' + target + '?';
  }

  fusionLink(): RouteLocationAsRelative {
    return {
      name: RouteEnum.Fusion,
      params: {
        id: this.left.numGroup,
        sourceLeft: useEntitiesStore().sourceLeft,
        sourceRight: useEntitiesStore().sourceRight,
        type: SynchroType.COURSE
      }
    };
  }

  getActions(): EntityAction[] {
    if (useEntitiesStore().sourceLeft === Source.AMMON) {
      return [
        EntityAction.success,
        EntityAction.fusion
      ];
    }
    return [
      EntityAction.success,
      EntityAction.fusion,
      EntityAction.create,
    ];
  }

  getLeftCardData(): CardData[] {
    return this.getCardData(this.left)
  }

  getRightCardData(): CardData[] {
    return this.getCardData(this.right)
  }

  match(filter: string): boolean {
    return this.left.product.toLowerCase().includes(filter.toLowerCase())
      || this.left.id.toLowerCase().includes(filter.toLowerCase())
      || this.left.numGroup.toLowerCase().includes(filter.toLowerCase())
      || this.left.site.toLowerCase().includes(filter.toLowerCase());
  }

  protected hydrateDetails(sourceDetails: SourceDetails[]): any {
    const details: CourseDetails = {
      id: '',
      product: '',
      numGroup: '',
      link: '',
      startDate: '',
      endDate: '',
      state: '',
      site: ''
    };

    sourceDetails.forEach((detail) => {
      switch (detail.label) {
        case '_id':
        case 'Référence EVA':
          details.id = String(detail.val)
          break;
        case 'Produit':
          details.product = String(detail.val)
          break;
        case 'N° groupe':
        case 'Référence':
          details.numGroup = String(detail.val)
          break;
        case 'Lien':
          details.link = this.filterLink(String(detail.val));
          break;
        case 'État':
          details.state = String(detail.val);
          break;
        case 'Date de début':
          if (String(detail.val) === '') {
            break;
          }

          details.startDate = new Date(String(detail.val)).toLocaleDateString('fr-FR');
          break;
        case 'Date de fin':
          if (String(detail.val) === '') {
            break;
          }
          details.endDate = new Date(String(detail.val)).toLocaleDateString('fr-FR');
          break;
        case 'site':
          details.site = String(detail.val);
          break;
        default:
        // console.warn(`Unknown label in course details: ${detail.label}`);
      }
    })

    return details
  }

  private getCardData(details: CourseDetails): CardData[] {
    return [
      {
        label: 'Id',
        value: details.id
      },
      {
        label: 'Produit',
        value: details.product
      },
      {
        label: 'N° de groupe',
        value: details.numGroup
      },
      {
        label: 'Début session',
        value: details.startDate
      },
      {
        label: 'Fin session',
        value: details.endDate
      },
      {
        label: 'Site',
        value: new CourseConfig().printField(details.site, 'Site')
      },
      // {
      //   label: 'Statut',
      //   value: details.state
      // },
    ];
  }
}
